import { createFileRoute } from "@tanstack/react-router";
import { OrderReceipt } from "@/components/order/order-receipt";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useTranslate } from "@/hooks/use-translate";
import { generateRandomString, pick } from "@/lib/utils";
import useAppStore from "@/store";
import type { Order } from "@/store/Order";
import type { Product } from "@/store/Product";
import type { PosSale, Sale } from "@/store/Sale";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import PosCart from "@/components/pos/pos-cart";
import PosPayments from "@/components/pos/pos-payments";
import Show from "@/components/ui/show";
import { Alert } from "@/components/ui/alert";
import type { Shop } from "@/store/Shop";
import type { Service } from "@/store/Service";
import ItemsGrid from "@/components/pos/items-grid";

export const Route = createFileRoute("/(dasboard)/pos")({
  component: PosPage,
});

const heightClass = "min-h-[750px]";

function PosPage() {
  const store = useAppStore();

  const [sales, setSales] = useState<PosSale[]>([]);

  const { t } = useTranslate();

  const contentA5Ref = useRef<HTMLDivElement>(null);
  const contentA6Ref = useRef<HTMLDivElement>(null);
  const printA5 = useReactToPrint({ contentRef: contentA5Ref });
  const printA6 = useReactToPrint({ contentRef: contentA6Ref });

  const order = store.order.current as Order;

  const form = useSimpleForm({
    discount: 0,
    delivery_fee: 0,
    tax: 0,
    shop_id: "",
    customer_id: "",
    user_id: "",
    payments: [],
  });

  useEffect(() => {
    store.category.fetch();
    store.financeAccount.fetch({
      shop_id: store.auth.session?.currentShop.id,
    });
  }, [store.auth.session]);

  useEffect(() => {
    setTimeout(() => {
      store.display.hide("sidebar");
    }, 100);
  }, []);

  const updateItem = (updates: Partial<PosSale>) => {
    setSales((items: PosSale[]) => {
      return items.map((item) => {
        if (item.id == updates.id) {
          return {
            ...item,
            ...updates,
          };
        } else {
          return item;
        }
      });
    });
  };

  const addSale = (item: Service | Product) => {
    // If the product already exist we just increment it.
    const sale = sales.find((sale) => {
      if (sale.salable_name == "StockBatch") {
        return sale.salable.product.id == item.id;
      } else {
        return sale.salable.id == item.id;
      }
    });
    if (sale) {
      updateItem({ id: sale.id, quantity: sale.quantity + 1 });
    } else {
      let sale: PosSale = {
        id: generateRandomString(),
        quantity: 1,
        selling_price: item.selling_price,
      } as PosSale;

      if (item.__type == "Product") {
        const batchs = item.stock_batches;
        sale = {
          ...sale,
          salable_id: batchs[0].id,
          salable: batchs[0],
          salable_name: "StockBatch",
          product: item,
          batchs: batchs,
        };
      } else if (item.__type == "Service") {
        sale = {
          ...sale,
          salable: item,
          salable_id: item.id,
          salable_name: "Service",
        };
      } else {
        return 0;
      }

      setSales((items: any) => {
        return [...items, sale];
      });
    }
  };

  const onDelete = (sale: Sale) => {
    setSales((items: Sale[]) => {
      return items.filter((item) => item.id != sale.id);
    });
  };

  const submit = async () => {
    store.errors.reset();

    if (sales.length == 0) {
      store.errors.set("sales", "Add sales");
      return undefined;
    }

    if (form.values.payments.length == 0) {
      store.errors.set("payments", "Add payments");
      return undefined;
    }

    store.errors.reset();
    const session = store.auth.session;

    const data = {
      ...form.values,
      status: "approved",
      delivery_status: "pending",
      payments: form.values.payments.map((item: any) => item),
      shop_id: session?.currentShop.id,
      user_id: session?.user.id,
      sales: sales.map((item) =>
        pick(item, ["salable_id", "salable_name", "selling_price", "quantity"])
      ),
    };

    store.order
      .create(data as any)
      .then((order) => {
        store.display.show("order_success");
        store.order.setCurrent(order);
        setTimeout(() => {
          if (store.auth?.session?.shop?.receipt_format == "A5") {
            printA5();
          } else {
            printA6();
          }
        }, 2000);
      })
      .catch(store.errors.catch);
  };

  return (
    <>
      {order && (
        <div className="hidden">
          <div ref={contentA5Ref}>
            <OrderReceipt
              order={order}
              shop={store.auth.session?.currentShop as Shop}
              size="A5"
            />
          </div>
          <div ref={contentA6Ref}>
            <OrderReceipt
              order={order}
              shop={store.auth.session?.currentShop as Shop}
              size="A6"
            />
          </div>
        </div>
      )}

      <Show
        when={store.errors.hasErrors() || store.display.visible.order_success}
      >
        <div className="fixed flex justify-center w-full z-40 top-4 right-4">
          <div className="w-[400px]">
            <> {store.errors.render()}</>

            <Alert
              autoClose={3000}
              message={t("Order proceed with success")}
              name={"order_success"}
            />
          </div>
        </div>
      </Show>

      <div className="mx-auto">
        <section className="overflow-h-auto grid grid-cols-1 lg:grid-cols-4 gap-4">
          <aside className="lg:col-span-2">
            <ItemsGrid heightClass={heightClass} addSale={addSale} />
          </aside>

          <aside className="col-span-1">
            <PosCart
              heightClass={heightClass}
              setSales={setSales}
              onDelete={onDelete}
              sales={sales}
              form={form}
              updateItem={updateItem}
            />
          </aside>

          <aside className="col-span-1">
            <PosPayments
              heightClass={heightClass}
              form={form}
              sales={sales}
              submit={submit}
            />
          </aside>
        </section>
      </div>
    </>
  );
}
